import React, { useEffect, useState } from 'react';
import { instrumentConfigApi } from '../../services/instrumentConfigApi';
import { formatInt } from '../../utils/numberFormat';

type Row = { id: string; label: string; cash: string | null; pp: string | null; free: string | null; over: string | null; error?: string };

/**
 * The account as the server reads it right now, shown before a bot starts (spec: manual flow,
 * "refetch the account data, then confirm"). Fetched when mounted, so opening the dialog is
 * the refetch.
 */
const AccountSnapshot: React.FC<{ accounts: { id: string; label: string }[] }> = ({ accounts }) => {
  const [rows, setRows] = useState<Row[] | null>(null);
  const key = accounts.map((a) => a.id).join(',');
  useEffect(() => {
    let live = true;
    Promise.all(
      accounts.map(async (a) => {
        try {
          const c = await instrumentConfigApi.getDepositCapacity(a.id);
          return { id: a.id, label: a.label, cash: c.cash, pp: c.purchasingPower, free: c.availableToDeposit, over: c.overAllocated ?? null };
        } catch (e) {
          return { id: a.id, label: a.label, cash: null, pp: null, free: null, over: null, error: (e as Error).message };
        }
      }),
    ).then((r) => live && setRows(r));
    return () => {
      live = false;
    };
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const v = (s: string | null) => (s == null ? '—' : formatInt(Number(s)));
  return (
    <div style={{ marginBottom: 16, fontSize: 12 }}>
      <div style={{ color: '#848E9C', marginBottom: 6 }}>Account, read just now — check it before the bot trades on it</div>
      {!rows ? (
        <div style={{ color: '#848E9C' }}>reading the account…</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#848E9C' }}>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Account</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>Cash</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>Purchasing power</th>
              <th style={{ textAlign: 'right', padding: '4px 8px' }}>Free to allocate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td style={{ padding: '4px 8px' }}>{r.label}</td>
                {r.error ? (
                  <td colSpan={3} style={{ color: '#F6465D', padding: '4px 8px' }}>{r.error}</td>
                ) : (
                  <>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{v(r.cash)}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px' }}>{v(r.pp)}</td>
                    <td style={{ textAlign: 'right', padding: '4px 8px', color: r.over && Number(r.over) > 0 ? '#F6465D' : undefined }}>
                      {r.over && Number(r.over) > 0 ? `over by ${v(r.over)}` : v(r.free)}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AccountSnapshot;
